import { Component, inject, output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { NotificationService } from '@app/core/services/notification.service';
import { BankIdVerificationDeliveryMethod } from '@app/sales/modules/details/models/private-customer.model';
import { ReinitiateBankIdVerificationAction } from '@app/sales/modules/details/states/customer.actions';
import { IceIconButtonComponent } from '@app/shared/components/atoms/ice-icon-button/ice-icon-button.component';
import { BankIdInput, BankIdVerificationDeliveryPayload } from '@app/shared/components/bankid-input/bankid-input.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { IconNames } from '@app/shared/enums/icon-names';
import { Store } from '@ngxs/store';
import { catchError, take, tap } from 'rxjs/operators';

type NewBankIdVerificationDialogData = {
  orderServiceId: number;
  initialPhoneNumber?: string | null;
  initialEmail?: string | null;
};
@Component({
  selector: 'app-new-bankid-verification-modal',
  imports: [MatDialogClose, MatDialogTitle, IceIconButtonComponent, MatDialogContent, IceAlertComponent, BankIdInput],
  templateUrl: './new-bankid-verification-modal.component.html',
  styleUrls: ['./new-bankid-verification-modal.component.scss'],
})
export class NewBankidVerificationModal {
  IconNames = IconNames;
  notificationService = inject(NotificationService);
  private store = inject(Store);
  private data = inject(MAT_DIALOG_DATA) as NewBankIdVerificationDialogData | null;
  private dialogRef = inject(MatDialogRef<NewBankidVerificationModal>);

  initialPhoneNumber = this.data?.initialPhoneNumber ?? null;
  initialEmail = this.data?.initialEmail ?? null;

  verificationSuccess = output<void>();

  sendNewBankIdLink({ method, msisdn, email }: BankIdVerificationDeliveryPayload) {
    if (!this.data?.orderServiceId) {
      this.notificationService.error('Fant ikke ordren som skal få ny BankID-lenke.');
      return;
    }

    const orderServiceId = this.data.orderServiceId.toString();
    const contactValue = (method === BankIdVerificationDeliveryMethod.SMS ? msisdn : email) ?? '';

    this.store
      .dispatch(new ReinitiateBankIdVerificationAction(orderServiceId, msisdn, email))
      .pipe(
        take(1),
        tap(() => {
          this.notificationService.success('BankID-forespørsel sendt!');
          this.verificationSuccess.emit();
          this.dialogRef.close(true);
        }),
        catchError(error => {
          if (error?.status === 400) {
            if (method === BankIdVerificationDeliveryMethod.SMS) {
              this.notificationService.error(
                `${contactValue} har feil format. Nummeret må starte med 4 eller 9, og være 8 siffer.`
              );
            } else {
              this.notificationService.error(`${contactValue} er ikke en gyldig e-postadresse.`);
            }
          } else if (error?.status === 500) {
            const genericError =
              'Noe gikk galt ved sending av ny BankID-lenke. Vennligst prøv igjen. Om problemet vedvarer, kontakt forhandlersupport.';
            this.notificationService.error(genericError);
          }
          throw error;
        })
      )
      .subscribe();
  }
}
